import * as React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import { useUrlParams } from '../../Hooks/useUrlParams';
import { GlobalStates } from '../../GlobalStates';
import { t } from 'i18next';

const MAX_FILTERS = 3;

export default function ListFilterItem({ paramName, label, options, optionsText }) {
    const { params, setParam } = useUrlParams();
    const { setAlert } = React.useContext(GlobalStates);
    
    // Get current filter values
    const currentValues = React.useMemo(() => {
        const values = params[paramName] || [];
        return values.map(v => v.toLowerCase());
    }, [params, paramName]);

    // Count active filter groups
    const activeFilterGroupsCount = React.useMemo(() => 
        Object.keys(params).length, 
        [params]
    );

    // Check if this filter group has any active filters
    const hasFiltersInGroup = React.useMemo(() => 
        currentValues.length > 0, 
        [currentValues]
    );
    
    // Safety check: ensure options is an array
    if (!Array.isArray(options)) {
        console.warn(`ListFilterItem: options for ${paramName} is not an array:`, options);
        return null;
    }

    const handleToggle = (value) => () => {
        const normalizedValue = value.toLowerCase();
        
        // Check if value is already selected
        const isSelected = currentValues.includes(normalizedValue);
        
        if (!isSelected) {
            // Check max filters limit
            if (activeFilterGroupsCount === MAX_FILTERS && !hasFiltersInGroup) {
                setAlert(t('max3filtrai'));
                return;
            }
            
            // Add new value
            setParam(paramName, [...currentValues, normalizedValue]);
        } else {
            // Remove value
            setParam(
                paramName, 
                currentValues.filter(v => v !== normalizedValue)
            );
        }
    };

    return (
        <List sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}>
            <h5>{label}</h5>
            {options.map((value, i) => {
                const labelId = `checkbox-list-label-${value}`;
                const normalizedValue = value.toLowerCase();
                const isSelected = currentValues.includes(normalizedValue);
                const isDisabled = !isSelected && 
                                 activeFilterGroupsCount === MAX_FILTERS && 
                                 !hasFiltersInGroup;

                return (
                    <ListItem
                        key={value}
                        secondaryAction={
                            <IconButton edge="end" aria-label="comments">
                            </IconButton>
                        }
                        disablePadding
                    >
                        <ListItemButton 
                            role={undefined} 
                            onClick={handleToggle(value)} 
                            dense
                            disabled={isDisabled}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={isSelected}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': labelId }}
                                    disabled={isDisabled}
                                />
                            </ListItemIcon>
                            <ListItemText id={labelId} primary={optionsText[i]} />
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
    );
}
